import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) { setErr("Passwords do not match"); return; }
    if (pw.length < 8) { setErr("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please log in.");
      navigate("/login", { replace: true });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-md text-center" data-testid="reset-missing-token">
          <h2 className="text-3xl font-black tracking-tight mb-3">Invalid link</h2>
          <p className="font-mono text-sm text-muted-foreground mb-6">
            This reset URL is missing a token. Please request a new reset email.
          </p>
          <Link to="/forgot-password" className="text-brand-gold font-bold underline underline-offset-4" data-testid="reset-goto-forgot">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-brand-gold text-white p-12">
        <Link to="/" className="flex items-center gap-2" data-testid="reset-logo">
          <div className="w-8 h-8 bg-card flex items-center justify-center">
            <Zap className="w-4 h-4 text-foreground" />
          </div>
          <span className="font-black text-lg tracking-tight">StenoSync</span>
        </Link>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-white/50 mb-6">Set new password</div>
          <h1 className="text-5xl font-black tracking-tight leading-none">
            Choose a<br />strong one.<br />
            <span className="text-brand-emerald">You earned it.</span>
          </h1>
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-bold">HIGH COURT PS training studio</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="reset-form">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Reset password</div>
          <h2 className="text-4xl font-black tracking-tight mb-2">New password</h2>
          <p className="font-mono text-sm text-muted-foreground mb-8">Enter your new password twice to confirm.</p>

          <div className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold">New Password (min 8 chars)</Label>
              <Input
                type="password" required minLength={8} maxLength={128}
                value={pw} onChange={(e) => setPw(e.target.value)}
                className="mt-2 rounded-lg border-border h-12 font-mono"
                data-testid="reset-password-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold">Confirm Password</Label>
              <Input
                type="password" required minLength={8} maxLength={128}
                value={pw2} onChange={(e) => setPw2(e.target.value)}
                className="mt-2 rounded-lg border-border h-12 font-mono"
                data-testid="reset-password-confirm-input"
              />
            </div>

            {err && (
              <div className="border-l-4 border-red-500 bg-red-50 px-4 py-2 text-sm font-mono text-red-700" data-testid="reset-error">
                {err}
              </div>
            )}

            <Button
              type="submit" disabled={loading}
              className="w-full rounded-lg h-12 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold transition-transform hover:-translate-y-0.5"
              data-testid="reset-submit-btn"
            >
              {loading ? "Updating…" : "Update password"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
